'use client'

import * as React from 'react'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  DayPicker,
  Dropdown,
  useDayPicker,
  type DropdownProps,
} from 'react-day-picker'

import { cn } from '../../../lib/utils'
import { buttonVariants } from '../Button'

export type CalendarProps = React.ComponentProps<typeof DayPicker>

const YEAR_INPUT_LENGTH = 4

function YearInput({
  value,
  onChange,
  className,
  style,
  'aria-label': ariaLabel,
}: DropdownProps) {
  const { fromDate, toDate } = useDayPicker()
  const year = Number(value)
  const [draft, setDraft] = React.useState<string | null>(null)

  const commit = (nextYear: number) => {
    const fromYear = fromDate?.getFullYear() ?? nextYear
    const toYear = toDate?.getFullYear() ?? nextYear
    const clamped = Math.min(Math.max(nextYear, fromYear), toYear)
    onChange?.({
      target: { value: String(clamped) },
    } as React.ChangeEvent<HTMLSelectElement>)
  }

  return (
    <div className={className} style={style}>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        aria-label={ariaLabel}
        maxLength={YEAR_INPUT_LENGTH}
        value={draft ?? year}
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '')
          if (digits.length === YEAR_INPUT_LENGTH) {
            setDraft(null)
            commit(Number(digits))
            return
          }
          setDraft(digits)
        }}
        onBlur={() => setDraft(null)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            setDraft(null)
          } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault()
            setDraft(null)
            commit(year + (e.key === 'ArrowUp' ? 1 : -1))
          }
        }}
        className="w-11 bg-transparent text-center text-sm font-medium tabular-nums outline-none selection:bg-primary selection:text-primary-foreground"
      />
    </div>
  )
}

function CaptionDropdown(props: DropdownProps) {
  return props.name === 'years' ? (
    <YearInput {...props} />
  ) : (
    <Dropdown {...props} />
  )
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months:
          'flex flex-col justify-center items-center sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'inline-flex items-center gap-0.5 text-sm font-medium',
        caption_dropdowns: 'flex items-center justify-center gap-0.5',
        dropdown_month:
          'relative inline-flex h-7 items-center rounded-md px-1 hover:bg-accent focus-within:ring-1 focus-within:ring-ring',
        dropdown_year:
          'relative inline-flex h-7 items-center rounded-md hover:bg-accent focus-within:ring-1 focus-within:ring-ring',
        dropdown:
          "absolute inset-0 z-10 w-full cursor-pointer appearance-none bg-popover text-sm text-popover-foreground opacity-0 [[data-theme='dark']_&]:[color-scheme:dark]",
        vhidden: 'sr-only',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell:
          'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
            : '[&:has([aria-selected])]:rounded-md',
        ),
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-8 w-8 p-0 font-normal aria-selected:opacity-100',
        ),
        day_range_start: 'day-range-start',
        day_range_end: 'day-range-end',
        day_selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        day_today:
          '[&:not([aria-selected])]:bg-accent [&:not([aria-selected])]:text-accent-foreground',
        day_outside:
          'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Dropdown: CaptionDropdown,
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn('h-4 w-4', className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn('h-4 w-4', className)} {...props} />
        ),
        IconDropdown: ({ className, ...props }) => (
          <ChevronDown
            className={cn('h-3 w-3 text-muted-foreground', className)}
            {...props}
          />
        ),
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
